import type { Patient, Reference } from '@medplum/fhirtypes'
import { CodeableConceptDisplay } from '@medplum/react'
import { referenceDisplay } from '@seen/core/display'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import {
  SDRStatus,
  sdrStatusToMetadata,
} from '@seen/core/models/tasks/sdr/utils'
import { Button } from '@seen/ui'
import {
  IconCalendarEvent,
  IconCalendarTime,
  IconDots,
  IconFilterCheck,
  IconUser,
  IconX,
} from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { compact, uniqBy } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { SDRTimeRemainingWithTooltip } from '#src/components/sdr/display/SDRTimeRemainingWithTooltip.tsx'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

const DEFAULT_STATUS_FILTER_VALUES = Object.values(SDRStatus).filter(
  (status) => status !== SDRStatus.WITHDRAWN
)

const NOTIFIABLE_SDR_STATUSES: readonly SDRStatus[] = [
  SDRStatus.APPROVED,
  SDRStatus.PARTIALLY_APPROVED,
  SDRStatus.DENIED,
  SDRStatus.PARTIALLY_DENIED,
  SDRStatus.IN_APPEAL,
  SDRStatus.WITHDRAWN,
]

export function SDRTable({
  patientRef,
}: {
  patientRef?: Reference<Patient>
}): JSX.Element {
  const navigate = useNavigateFromClickEvent()
  const patchSDR = usePatchMutation<SDR>().mutate
  const openConfirmation = useDeleteConfirmationStore((state) => state.open)
  const [filters, setFilters] = useState<Filter[]>([])

  const { data: sdrs } = useSearch(
    SDR,
    patientRef ? SDR.queryByParticipant(patientRef) : SDR.searchQuery()
  )

  const participantOptions = useMemo(() => {
    return uniqBy(
      (sdrs ?? []).flatMap((sdr) => {
        const participant = sdr.for
        if (!participant.reference) {
          return []
        }
        return {
          label: <ParticipantOption participant={participant} />,
          value: participant.reference,
          keywords: [referenceDisplay(participant) ?? ''],
        }
      }),
      'value'
    )
  }, [sdrs])

  const statusOptions = useMemo(() => {
    return Object.values(SDRStatus).map((status) => ({
      label: sdrStatusToMetadata[status].label,
      value: status,
      keywords: [sdrStatusToMetadata[status].label],
    }))
  }, [])

  const filterOptions = useMemo<FilterConfigMap<SDR>>(() => {
    const participantFilter: FilterConfigMap<SDR> = {}
    if (!patientRef) {
      participantFilter.participant = {
        label: 'Participant',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: participantOptions,
        valueFn: (sdr) => sdr.for.reference ?? '',
      }
    }
    return {
      ...participantFilter,
      status: {
        label: 'Status',
        icon: <IconFilterCheck className="size-4" />,
        type: 'multi-select',
        options: statusOptions,
        valueFn: (sdr) => sdr.status ?? '',
      },
      dueDate: {
        label: 'Due Date',
        icon: <IconCalendarTime className="size-4" />,
        type: 'date',
        valueFn: (sdr) => sdr.decisionNotificationDate?.toISOString() ?? '',
      },
      receivedDate: {
        label: 'Received Date',
        icon: <IconCalendarEvent className="size-4" />,
        type: 'date',
        valueFn: (sdr) => sdr.receiptDate?.toISOString() ?? '',
      },
    }
  }, [patientRef, participantOptions, statusOptions])

  const hasStatusFilter = filters.some((filter) => filter.field === 'status')
  const effectiveFilters = hasStatusFilter
    ? filters
    : [
        ...filters,
        {
          field: 'status',
          value: DEFAULT_STATUS_FILTER_VALUES,
        },
      ]

  const filteredSdrs = useFilteredData(sdrs, filterOptions, effectiveFilters)

  const columns: ColumnDef<SDR>[] = useMemo(
    () =>
      compact([
        !patientRef && {
          accessorKey: 'participant',
          header: 'Participant',
          cell: ({ row }) => (
            <ParticipantNameplate participant={row.original.for} />
          ),
        },
        {
          accessorKey: 'sdr',
          header: 'SDR',
          cell: ({ row }) =>
            extractTextFromHTML(row.original.title ?? row.original.description),
        },
        {
          id: 'status',
          header: 'Status',
          cell: ({ row }) => (
            <CodeableConceptDisplay value={row.original.businessStatus} />
          ),
        },
        {
          id: 'requester',
          header: 'Requester',
          cell: ({ row }) => {
            const requester = row.original.requester
            if (requester?.reference === row.original.for.reference) {
              return 'Self'
            }
            return referenceDisplay(requester)
          },
        },
        {
          id: 'dueDate',
          header: 'Due Date',
          cell: ({ row }) => {
            const sdr = row.original
            if (sdr.status === SDRStatus.WITHDRAWN) {
              return null
            }
            return <SDRTimeRemainingWithTooltip sdr={sdr} as="p" />
          },
        },
        {
          id: 'notified',
          header: 'Notified?',
          cell: ({ row }) => {
            const sdr = row.original
            const sdrStatus = sdr.status
            if (!sdrStatus || !NOTIFIABLE_SDR_STATUSES.includes(sdrStatus)) {
              return null
            }
            return <>{sdr.notificationDate ? 'Yes' : 'No'}</>
          },
        },
        {
          accessorKey: 'received',
          header: 'Received',
          cell: ({ row }) =>
            row.original.receiptDate ? (
              <HoverDate date={row.original.receiptDate} />
            ) : null,
        },
        {
          id: 'actions',
          cell: ({ row }) => {
            const sdr = row.original
            const isWithdrawn = sdr.status === SDRStatus.WITHDRAWN
            const isApproved = sdr.status === SDRStatus.APPROVED
            return (
              <div className="flex items-center justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="-my-3">
                      <IconDots className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenuItem
                      disabled={isWithdrawn || isApproved}
                      className="text-red-600"
                      onSelect={() => {
                        openConfirmation({
                          title: 'Revoke SDR?',
                          description: `Are you sure you want to revoke "${extractTextFromHTML(sdr.title ?? sdr.description)}"? This action will mark the SDR as withdrawn.`,
                          deleteText: 'Revoke',
                          onConfirm: () => {
                            patchSDR({
                              instance: sdr,
                              mutateFn: (s) => {
                                s.state = SDRStatus.WITHDRAWN
                              },
                            })
                          },
                        })
                      }}
                    >
                      <IconX className="size-4" />
                      Revoke SDR…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )
          },
        },
      ]),
    [patientRef, openConfirmation, patchSDR]
  )

  if (!sdrs) {
    return <Loader />
  }

  return (
    <div className="space-y-2">
      <FilterComposer<SDR>
        id="sdr-table-filters"
        filterOptions={filterOptions}
        onChange={setFilters}
      />
      <DataTable
        columns={columns}
        data={filteredSdrs ?? []}
        onRowClick={(sdr, e) =>
          navigate(`/${sdr.for.reference}/sdr/${sdr.id}`, e)
        }
      />
    </div>
  )
}
